import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    Tabs,
    Tab
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    FileDownload as ExportIcon
} from '@mui/icons-material';

/**
 * Drill-Down Dialog Component
 * 
 * Modal for displaying detailed data when user clicks on a KPI or chart element.
 * Supports:
 * - Sortable/searchable table
 * - Multiple tabs for different views
 * - Export functionality
 * - Pagination
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Dialog title
 * @param {string} props.subtitle - Optional subtitle
 * @param {Array} props.data - Data to display
 * @param {Array} props.columns - Column configuration [{ key, label, format, align }]
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onExport - Export handler
 * @param {Array} props.tabs - Tab configuration for multiple views
 * @param {Object} props.summary - Summary statistics to show at top
 */
const DrillDownDialog = ({
    open,
    onClose,
    title,
    subtitle,
    data = [],
    columns = [],
    loading = false,
    onExport,
    tabs = null,
    summary = null
}) => {
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Sort state
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    
    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Handle sort
    const handleSort = (column) => {
        const isAsc = orderBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter data by search term
    const filteredData = React.useMemo(() => {
        if (!searchTerm) return data;
        
        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(row => 
            columns.some(col => {
                const value = row[col.key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(lowerSearch);
            })
        );
    }, [data, searchTerm, columns]);

    // Sort data
    const sortedData = React.useMemo(() => {
        if (!orderBy) return filteredData;
        
        return [...filteredData].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];
            
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;
            
            let comparison = 0;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else {
                comparison = String(aVal).localeCompare(String(bVal));
            }
            
            return order === 'asc' ? comparison : -comparison;
        });
    }, [filteredData, orderBy, order]);

    // Paginate data
    const paginatedData = sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Format cell value
    const formatCellValue = (value, column) => {
        if (value === null || value === undefined) return 'N/A';
        
        if (column.format) {
            return column.format(value);
        }
        
        // Auto-detect formatting
        if (typeof value === 'number') {
            // Check if it looks like currency (large number or ends in .00)
            if (Math.abs(value) > 100) {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                }).format(value);
            }
            // Check if it looks like a percentage
            if (value >= 0 && value <= 1) {
                return `${(value * 100).toFixed(1)}%`;
            }
            return value.toLocaleString();
        }
        
        return String(value);
    };

    // Get status color for certain columns
    const getCellColor = (value, column) => {
        if (column.colorFn) {
            return column.colorFn(value);
        }
        
        // Auto-detect color for common patterns
        if (column.key.includes('profit') || column.key.includes('margin')) {
            if (typeof value === 'number') {
                return value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary';
            }
        }
        
        if (column.key === 'status') {
            switch (value) {
                case 'active': return 'success.main';
                case 'completed': return 'primary.main';
                case 'on_hold': return 'warning.main';
                default: return 'text.primary';
            }
        }
        
        return 'text.primary';
    };

    // Render summary section
    const renderSummary = () => {
        if (!summary) return null;
        
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    mb: 2, 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1 
                }}
            >
                {Object.entries(summary).map(([key, value]) => (
                    <Box key={key}>
                        <Typography variant="caption" color="text.secondary">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {typeof value === 'number' ? value.toLocaleString() : value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h6">{title}</Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: 300
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Tabs if provided */}
                        {tabs && tabs.length > 1 && (
                            <Tabs 
                                value={activeTab} 
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{ mb: 2 }}
                            >
                                {tabs.map((tab, index) => (
                                    <Tab key={index} label={tab.label} />
                                ))}
                            </Tabs>
                        )}

                        {/* Summary */}
                        {renderSummary()}

                        {/* Search */}
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ width: 250 }}
                            />
                            <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ ml: 2 }}
                            >
                                {filteredData.length} of {data.length} records
                            </Typography>
                        </Box>

                        {/* Data table */}
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.key}
                                                align={column.align || 'left'}
                                                sortDirection={orderBy === column.key ? order : false}
                                                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === column.key}
                                                    direction={orderBy === column.key ? order : 'asc'}
                                                    onClick={() => handleSort(column.key)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.length === 0 ? (
                                        <TableRow>
                                            <TableCell 
                                                colSpan={columns.length} 
                                                align="center"
                                                sx={{ py: 4 }}
                                            >
                                                <Typography color="text.secondary">
                                                    No data found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedData.map((row, rowIndex) => (
                                            <TableRow 
                                                key={row.id || rowIndex}
                                                hover
                                                sx={{ 
                                                    '&:nth-of-type(odd)': { 
                                                        bgcolor: 'grey.50' 
                                                    } 
                                                }}
                                            >
                                                {columns.map((column) => (
                                                    <TableCell
                                                        key={column.key}
                                                        align={column.align || 'left'}
                                                        sx={{ 
                                                            color: getCellColor(row[column.key], column)
                                                        }}
                                                    >
                                                        {column.key === 'status' ? (
                                                            <Chip
                                                                label={row[column.key]}
                                                                size="small"
                                                                color={
                                                                    row[column.key] === 'active' ? 'success' :
                                                                    row[column.key] === 'completed' ? 'primary' :
                                                                    row[column.key] === 'on_hold' ? 'warning' :
                                                                    'default'
                                                                }
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />
                                                        ) : (
                                                            formatCellValue(row[column.key], column)
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {onExport && (
                    <Button
                        startIcon={<ExportIcon />}
                        onClick={() => onExport(sortedData)}
                        disabled={loading || sortedData.length === 0}
                    >
                        Export to CSV
                    </Button>
                )}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DrillDownDialog;

